// native
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

// models
import { PhoneOS } from '../../../models';
import { NetworkStepResult } from '../../home-management.types';

@Component({
  selector: 'app-home-network-step',
  templateUrl: './network-step.component.html',
  standalone: false
})
export class NetworkStepComponent implements OnInit, OnDestroy {
  @Input() initial: NetworkStepResult | null = null;
  @Output() next: EventEmitter<NetworkStepResult> = new EventEmitter<NetworkStepResult>();
  @Output() back: EventEmitter<void> = new EventEmitter<void>();

  form: UntypedFormGroup;

  readonly phoneOptions: PhoneOS[] = ['iOS', 'Android'];

  private hotspotSubscription: Subscription;

  constructor(private formBuilder: UntypedFormBuilder) { }

  ngOnInit(): void {
    // Control names deliberately avoid "ssid"/"password" so Chrome doesn't
    // mistake these for WiFi/login credentials and autofill them.
    this.form = this.formBuilder.group({
      phone_os: [this.initial?.phone_os ?? 'iOS'],
      // hotspot_enabled is boolean; Validators.required would reject `false`,
      // so it is gated via canProceed instead.
      hotspot_enabled: [this.initial?.hotspot_enabled ?? null],
      wifi_network_name: [this.initial?.wifi_ssid ?? null, Validators.required],
      wifi_network_key: [this.initial?.wifi_password ?? null, Validators.required]
    });

    this.updatePhoneOsValidators(this.form.get('hotspot_enabled').value);
    this.hotspotSubscription = this.form.get('hotspot_enabled').valueChanges.subscribe(
      enabled => this.updatePhoneOsValidators(enabled)
    );
  }

  ngOnDestroy(): void {
    this.hotspotSubscription?.unsubscribe();
  }

  get isHotspotEnabled(): boolean {
    return this.form?.get('hotspot_enabled')?.value === true;
  }

  private updatePhoneOsValidators(hotspotEnabled: boolean | null): void {
    const phoneOs = this.form.get('phone_os');
    if (hotspotEnabled === true) {
      phoneOs.setValidators(Validators.required);
    } else {
      phoneOs.clearValidators();
      if (!phoneOs.value)
        phoneOs.setValue('iOS');
    }
    phoneOs.updateValueAndValidity();
  }

  setPhoneOs(value: PhoneOS): void {
    this.form.get('phone_os').setValue(value);
  }

  get canProceed(): boolean {
    return !!this.form && this.form.valid && this.form.get('hotspot_enabled').value !== null;
  }

  onNext(): void {
    if (!this.canProceed)
      return;

    const v = this.form.value;
    this.next.emit({
      phone_os: v.phone_os,
      hotspot_enabled: v.hotspot_enabled,
      wifi_ssid: v.wifi_network_name,
      wifi_password: v.wifi_network_key
    });
  }
}
